/**
 * The Cash out tab: enter Shrooms, see EUR conversion, file a withdrawal.
 *
 * Amount is entered in Shrooms (the premium currency). Since 1 Shroom = 1 cent,
 * the integer maps directly to EUR cents for the core withdrawal API. The UI
 * shows the EUR equivalent after the fee so the player knows what arrives.
 *
 * Receiver details (card or IBAN) are collected here; first/last name are
 * pre-filled from the player's saved personal details (Account tab).
 *
 * Submits via core's POST /api/payments/withdrawals which triggers the actual
 * ISE gateway payout — not the game-layer manual-approval endpoint.
 */
import { el, toast } from './dom';
import { mountCardBrands, termsConsentRow } from './ui/legal';
import { ApiError } from '../core/network/api';
import { shopApi, accountApi, type Wallet, type PersonalDetails } from './game-api';
import { getPaymentMethods, getWithdrawalLimits, requestWithdrawal, toPaymentError } from '../core/payments/payments-api';
import { feeCents, formatMoney } from '../core/payments/methods';
import type { PayoutMethod, WithdrawalLimits } from '../core/payments/types';
import {
  formatCardInput, formatIbanInput, formatBicInput,
  isValidCardNumber, isValidIban, isValidBic,
  normaliseCardNumber, normaliseIban, normaliseBic,
} from '../core/payments/payout-validation';
import { missingFields, normaliseInput, validate, type BillingAddress, type BillingField } from '../core/payments/billing-address';

const CARD = 'rounded-xl border border-border bg-surface-2/60 p-3.5';
const LABEL = 'text-[11px] uppercase tracking-wide text-text-dim';
const INPUT = 'w-full bg-surface-2 border border-border rounded px-2.5 py-2 text-sm outline-none focus:border-gold';
const BTN_GOLD = 'px-3 py-2 rounded bg-gold text-bg font-bold text-sm hover:brightness-105 active:scale-95 transition disabled:opacity-50';
const ERR = 'text-[12px] text-error mt-0.5';

/** The server's wording for the same refusal (BILLING_ADDRESS_REQUIRED). */
export const BILLING_REQUIRED_TEXT = 'Please complete your billing address in Settings before requesting a withdrawal';

const ADDRESS_FIELDS: readonly BillingField[] = ['line1', 'city', 'zip', 'state', 'country'];

/**
 * Whether the saved account address is complete enough for a withdrawal — the
 * server's rule mirrored for instant feedback: ISO-2 country, street 3–50,
 * city 3–50, ZIP 3–10, and a state code for US / CA / AU. The server still
 * decides; this only saves a round trip that would end in the same refusal.
 */
export function billingAddressReady(personal: PersonalDetails | null): boolean {
  const addr: BillingAddress = {
    firstName: null, lastName: null, line2: null, line1: null, city: null, zip: null, state: null, country: null,
    ...normaliseInput({
      line1: personal?.address, city: personal?.city, zip: personal?.zipCode,
      state: personal?.stateCode, country: personal?.country,
    }),
  };
  const errors = validate(addr);
  return !missingFields(addr).some((f) => ADDRESS_FIELDS.includes(f))
    && !ADDRESS_FIELDS.some((f) => errors[f]);
}

export interface CashoutOptions {
  /** Opens Settings → Account, where the billing address is edited. */
  onOpenAccount?: () => void;
}

/**
 * Whether to show the Cash out screen at all — the operator's general
 * withdrawals switch (game `/shop/withdrawals`), not whether a payout method
 * happens to be on: with the switch on and no method, the screen says so.
 */
export async function cashoutOpen(): Promise<boolean> {
  return shopApi.withdrawals().then((w) => w.enabled).catch(() => false);
}

const fmt = (n: number) => n.toLocaleString();
const fmtEur = (cents: number) => formatMoney(cents, 'EUR');

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    if (e.message?.includes('billing') || e.message?.includes('Billing'))
      return BILLING_REQUIRED_TEXT;
    return e.message || fallback;
  }
  const pe = toPaymentError(e);
  switch (pe.code) {
    case 'BILLING_ADDRESS_REQUIRED': return BILLING_REQUIRED_TEXT;
    case 'KYC_REQUIRED': return 'Identity verification is required before withdrawing.';
    case 'WITHDRAWALS_DISABLED': return 'Withdrawals are currently unavailable.';
    case 'PAYOUT_METHOD_UNAVAILABLE': return 'This payout method is not available right now.';
    case 'INVALID_RECEIVER': return 'The card number or IBAN is not valid.';
    case 'INSUFFICIENT_FUNDS': return 'You do not have enough withdrawable balance.';
    case 'WITHDRAWAL_IN_PROGRESS': return 'You already have a withdrawal in progress.';
    case 'AMOUNT_OUT_OF_RANGE': return 'The amount is outside the allowed range.';
    default: return pe.message || fallback;
  }
}

export function renderCashout(body: HTMLElement, opts: CashoutOptions = {}) {
  body.replaceChildren(el('div', 'py-8 text-center text-sm text-text-dim', {}, 'Loading…'));

  Promise.all([shopApi.wallet(), getPaymentMethods(), getWithdrawalLimits(), accountApi.profile()])
    .then(([wallet, pm, limits, profile]) =>
      draw(body, wallet, pm.withdrawals, limits, profile.personal ?? null, opts))
    .catch(() => body.replaceChildren(
      el('div', 'py-6 text-center text-sm text-error', {}, 'Could not load your cash-out details.')));
}

function draw(
  body: HTMLElement,
  wallet: Wallet,
  withdrawals: { enabled: boolean; methods: PayoutMethod[] },
  limits: WithdrawalLimits,
  personal: PersonalDetails | null,
  opts: CashoutOptions,
) {
  const verified = wallet.kycStatus === 'APPROVED';

  const balances = el('div', `${CARD} grid grid-cols-2 gap-3`, {},
    stat('Shrooms', fmt(wallet.shrooms)),
    stat('Withdrawable', `${fmt(wallet.withdrawable)} (${fmtEur(wallet.withdrawable)})`),
    stat('Deposited', fmt(wallet.lifetimeDeposited)),
    stat('Withdrawn', fmt(wallet.lifetimeWithdrawn)));

  const parts: HTMLElement[] = [
    el('div', LABEL, {}, 'Your balance'),
    balances,
    el('div', 'text-[11px] text-text-dim', {},
      'Only Shrooms you paid for can be cashed out. Withdrawal fees follow our '
      + 'Pay-In & Pay-Out policy, with no hidden or extra fees.'),
  ];

  if (!verified) {
    parts.push(el('div', LABEL, {}, 'Identity'), kycCard(wallet.kycStatus, () => renderCashout(body, opts)));
  }

  parts.push(el('div', LABEL, {}, 'Withdraw'));

  if (!withdrawals.methods.length) {
    // The operator has cash-out open (that is why this tab exists) but no payout
    // method is switched on: say so, rather than letting the tab vanish.
    parts.push(el('div', `${CARD} text-sm text-text-dim`, { dataset: { noMethods: '' } },
      'No withdrawal methods are available right now. Please try again later.'));
  } else {
    const billingReady = billingAddressReady(personal);
    if (!billingReady) parts.push(billingNotice(opts));
    parts.push(withdrawForm(withdrawals.methods, limits, personal, billingReady, () => renderCashout(body, opts)));
  }

  body.replaceChildren(el('div', 'space-y-3', {}, ...parts));
}

function withdrawForm(
  methods: PayoutMethod[],
  limits: WithdrawalLimits,
  personal: PersonalDetails | null,
  billingReady: boolean,
  reload: () => void,
): HTMLElement {
  const minShrooms = limits.minCents;
  const maxShrooms = Math.min(limits.maxCents, limits.availableCents);
  const bps = limits.feeBps;

  let method: PayoutMethod | null = methods[0] ?? null;

  const wrap = el('div', CARD);

  const eurPreview = el('div', 'text-sm text-gold font-mono mt-1');
  const feePreview = el('div', 'text-[11px] text-text-dim mt-0.5');
  const errMsg = el('div', ERR, { style: 'display:none' });

  const amountInput = el('input', INPUT, {
    type: 'text', inputmode: 'numeric', placeholder: '0', autocomplete: 'off',
  }) as HTMLInputElement;

  const receiverInput = el('input', INPUT, {
    type: 'text', inputmode: 'text', autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false',
  }) as HTMLInputElement;

  const bicWrap = el('div', '', { style: 'display:none' });
  const bicInput = el('input', INPUT, {
    type: 'text', inputmode: 'text', autocomplete: 'off', autocapitalize: 'characters', spellcheck: 'false',
    maxlength: '11', placeholder: 'BPKOPLPW',
  }) as HTMLInputElement;
  bicWrap.append(
    el('div', LABEL, {}, 'BIC / SWIFT'),
    bicInput,
  );

  const receiverLabel = el('div', LABEL);

  const submitBtn = el('button', BTN_GOLD, {}, 'Request withdrawal') as HTMLButtonElement;
  // Same rule as the shop checkout: no transaction without accepting the Terms. An open request
  // or an incomplete billing address keeps the button off whatever the consent box says.
  const consent = termsConsentRow((ok) => { submitBtn.disabled = !ok || limits.hasOpenRequest || !billingReady; });
  submitBtn.disabled = true;
  const cardBrands = el('div', 'mt-3 empty:hidden');
  mountCardBrands(cardBrands);

  const methodBtns = methods.map((m) =>
    el('button', 'flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 bg-surface border rounded touch-manipulation transition-colors duration-[120ms]',
      { type: 'button', dataset: { method: m.id } }, m.label));

  function selectMethod(m: PayoutMethod) {
    method = m;
    methodBtns.forEach((btn) => {
      const on = btn.dataset.method === m.id;
      btn.classList.toggle('border-gold', on);
      btn.classList.toggle('shadow-glow', on);
      btn.classList.toggle('border-border', !on);
    });
    const isCard = m.receiver === 'cardNumber';
    // The brand badges describe a card payout; they mean nothing next to an IBAN.
    cardBrands.style.display = isCard ? '' : 'none';
    receiverLabel.textContent = isCard ? 'Card number' : 'IBAN';
    receiverInput.inputMode = isCard ? 'numeric' : 'text';
    receiverInput.value = '';
    bicWrap.style.display = isCard ? 'none' : '';
  }

  methodBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const m = methods.find((x) => x.id === btn.dataset.method);
      if (m) selectMethod(m);
    });
  });

  function updatePreview() {
    const raw = amountInput.value.replace(/[^0-9]/g, '');
    const shrooms = raw ? parseInt(raw, 10) : 0;
    if (shrooms <= 0) {
      eurPreview.textContent = '';
      feePreview.textContent = '';
      return;
    }
    const fee = feeCents(shrooms, bps);
    const net = shrooms - fee;
    eurPreview.textContent = `≈ ${fmtEur(net)}`;
    feePreview.textContent = `${fmt(shrooms)} Shrooms − ${fmtEur(fee)} fee = ${fmtEur(net)} payout`;
  }

  amountInput.addEventListener('input', () => {
    amountInput.value = amountInput.value.replace(/[^0-9]/g, '');
    updatePreview();
    errMsg.style.display = 'none';
  });

  receiverInput.addEventListener('input', () => {
    receiverInput.value = method?.receiver === 'cardNumber'
      ? formatCardInput(receiverInput.value)
      : formatIbanInput(receiverInput.value);
  });
  bicInput.addEventListener('input', () => {
    bicInput.value = formatBicInput(bicInput.value);
  });

  function showErr(text: string) {
    errMsg.textContent = text;
    errMsg.style.display = '';
  }

  submitBtn.addEventListener('click', async () => {
    errMsg.style.display = 'none';
    if (!method) return;
    if (!consent.isAccepted()) { showErr('Please accept the Terms of Service first.'); return; }

    const raw = amountInput.value.replace(/[^0-9]/g, '');
    const shrooms = raw ? parseInt(raw, 10) : 0;

    if (shrooms <= 0) { showErr('Enter amount in Shrooms'); return; }
    if (shrooms < minShrooms) { showErr(`Minimum is ${fmt(minShrooms)} Shrooms (${fmtEur(minShrooms)})`); return; }
    if (shrooms > maxShrooms) { showErr(`Maximum is ${fmt(maxShrooms)} Shrooms (${fmtEur(maxShrooms)})`); return; }

    if (!billingReady) { showErr(BILLING_REQUIRED_TEXT); return; }

    const firstName = personal?.firstName;
    const lastName = personal?.lastName;
    if (!firstName || !lastName) {
      showErr('Please fill in your name in the Account settings first.');
      return;
    }

    const isCard = method.receiver === 'cardNumber';
    if (isCard) {
      if (!isValidCardNumber(normaliseCardNumber(receiverInput.value))) {
        showErr('Enter a valid card number');
        return;
      }
    } else {
      if (!isValidIban(normaliseIban(receiverInput.value))) {
        showErr('Enter a valid IBAN');
        return;
      }
      if (!isValidBic(normaliseBic(bicInput.value))) {
        showErr('Enter a valid BIC / SWIFT code');
        return;
      }
    }

    submitBtn.disabled = true;
    try {
      await requestWithdrawal({
        method: method.id,
        amountCents: shrooms,
        firstName,
        lastName,
        billing: {
          line1: personal?.address ?? undefined,
          city: personal?.city ?? undefined,
          zip: personal?.zipCode ?? undefined,
          state: personal?.stateCode ?? undefined,
          country: personal?.country ?? undefined,
        },
        ...(isCard
          ? { cardNumber: normaliseCardNumber(receiverInput.value) }
          : { iban: normaliseIban(receiverInput.value), bankNumber: normaliseBic(bicInput.value) }),
      });
      toast('Withdrawal requested');
      reload();
    } catch (e) {
      submitBtn.disabled = !consent.isAccepted();
      showErr(errorMessage(e, 'Could not submit withdrawal'));
    }
  });

  if (method) selectMethod(method);

  const nameNote = (!personal?.firstName || !personal?.lastName)
    ? el('div', 'text-[12px] text-amber-400', {},
        'Your name is not set — please fill it in under Account settings before withdrawing.')
    : el('div', 'text-[12px] text-text-dim', {},
        `Receiver: ${personal.firstName} ${personal.lastName}`);

  wrap.append(
    el('div', 'text-sm text-text-dim mb-2', {}, 'Withdrawals are processed in EUR.'),
    el('div', 'flex gap-2 mb-3', { role: 'radiogroup' }, ...methodBtns),
    nameNote,
    el('div', 'mt-3', {},
      receiverLabel,
      receiverInput,
    ),
    bicWrap,
    el('div', 'mt-3', {},
      el('div', LABEL, {}, 'Amount (Shrooms)'),
      amountInput,
      eurPreview,
      feePreview,
      el('div', 'text-[11px] text-text-dim mt-1', {},
        `Min ${fmt(minShrooms)} · Max ${fmt(maxShrooms)} Shrooms`),
    ),
    errMsg,
    cardBrands,
    el('div', 'mt-3', {}, consent.row),
    el('div', 'mt-2 flex justify-end', {}, submitBtn),
    ...(limits.hasOpenRequest
      ? [el('div', 'text-[13px] text-text-dim mt-1', {}, 'You already have a withdrawal in progress.')]
      : []),
  );

  return wrap;
}

/** Shown above the form while the account has no complete billing address. */
function billingNotice(opts: CashoutOptions): HTMLElement {
  return el('div', `${CARD} text-sm text-amber-400`, { role: 'status', dataset: { billingNotice: '' } },
    el('div', '', {}, BILLING_REQUIRED_TEXT + '.'),
    opts.onOpenAccount
      ? el('button', 'mt-2 min-h-[44px] underline text-gold', { type: 'button', onClick: () => opts.onOpenAccount?.() },
        'Open Settings → Account')
      : el('div', 'mt-1 text-[12px] text-text-dim', {}, 'Settings → Account → billing address'));
}

function stat(label: string, value: string): HTMLElement {
  return el('div', '', {},
    el('div', LABEL, {}, label),
    el('div', 'text-sm font-mono', {}, value));
}

// ─── identity ────────────────────────────────────────────────────────────────

function kycCard(status: string, reload: () => void): HTMLElement {
  if (status === 'APPROVED') {
    return el('div', `${CARD} text-sm text-leaf`, {}, 'Your identity is verified.');
  }
  if (status === 'PENDING') {
    return el('div', `${CARD} text-sm text-text-dim`, {},
      'Your documents are with us — verification usually takes a couple of days.');
  }

  const fullName = el('input', INPUT, { placeholder: 'Full name, as on the document', autocomplete: 'name' }) as HTMLInputElement;
  const dob = el('input', INPUT, { type: 'date', autocomplete: 'bday' }) as HTMLInputElement;
  const address = el('input', INPUT, { placeholder: 'Residential address', autocomplete: 'street-address' }) as HTMLInputElement;
  const country = el('input', INPUT, { placeholder: 'Country code (e.g. EE)', maxlength: '2', autocomplete: 'country', style: 'text-transform: uppercase' }) as HTMLInputElement;
  const file = el('input', 'text-xs text-text-dim', { type: 'file', accept: 'image/*,application/pdf' }) as HTMLInputElement;
  const msg = el('div', 'text-[12px] min-h-[16px]');
  const submit = el('button', BTN_GOLD, {}, 'Submit for verification') as HTMLButtonElement;

  const say = (text: string, ok = false) => {
    msg.textContent = text;
    msg.className = `text-[12px] min-h-[16px] ${ok ? 'text-leaf' : 'text-error'}`;
  };

  submit.addEventListener('click', async () => {
    const document = file.files?.[0];
    if (!fullName.value.trim()) { say('Enter your full name'); return; }
    if (!dob.value) { say('Enter your date of birth'); return; }
    if (!address.value.trim()) { say('Enter your address'); return; }
    if (!/^[A-Za-z]{2}$/.test(country.value.trim())) { say('Country must be a two-letter code, like EE'); return; }
    if (!document) { say('Attach a photo or scan of your ID'); return; }
    if (document.size > 10_000_000) { say('The document must be under 10 MB'); return; }

    submit.disabled = true;
    try {
      await shopApi.submitKyc({
        fullName: fullName.value.trim(),
        dateOfBirth: dob.value,
        address: address.value.trim(),
        country: country.value.trim().toUpperCase(),
        document,
      });
      toast('Documents submitted');
      reload();
    } catch (e) {
      say(errorMessage(e, 'Could not submit your documents'));
      submit.disabled = false;
    }
  });

  return el('div', `${CARD} space-y-2`, {},
    el('div', 'text-[12px] text-text-dim', {},
      status === 'REJECTED'
        ? 'Your last submission was rejected. Check the details and try again.'
        : 'We need to confirm who you are before paying money out.'),
    fullName, dob, address, country,
    el('div', '', {}, el('div', LABEL, {}, 'ID document'), file),
    msg,
    el('div', 'flex justify-end', {}, submit));
}
